import React, { useRef, useState } from 'react';
import { Modal, TextInput, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faComment } from '@fortawesome/free-regular-svg-icons';
import { faLock, faPencil } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../../../../../components/Button';
import { ordersService } from '../../../../../services/orders/orders.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';
import { noSelectionTextInputProps } from '../../receipt/receipt-input.utils';
import classes from './table-description.module.scss';

// Mirrors updateTableDescriptionSchema (orders.dto.ts).
const MAX_LENGTH = 60;
const QUICK_WORDS = ['זוג', 'משפחה', 'ילדים', 'יום הולדת', 'אלרגיה'];

type TableDescriptionProps = {
  tenantId: string;
  employeeSession: string;
  orderId: number;
  tableNumber: string | null;
  description: string | null;
};

// Summary: Staff-only table description strip above the cart, with its edit dialog.
const TableDescriptionComponent: React.FC<TableDescriptionProps> = ({
  tenantId,
  employeeSession,
  orderId,
  tableNumber,
  description,
}) => {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const baselineRef = useRef<string | null>(null);

  const open = () => {
    baselineRef.current = description;
    setDraft(description ?? '');
    setOpened(true);
  };

  const appendQuickWord = (word: string) => {
    setDraft((previous) => {
      const base = previous.trim();
      return (base.length === 0 ? word : `${base}, ${word}`).slice(0, MAX_LENGTH);
    });
  };

  const handleSave = async () => {
    const next = draft.trim() || null;
    if (next === baselineRef.current) {
      setOpened(false);
      return;
    }
    setIsSaving(true);
    try {
      await ordersService.updateTableDescription(tenantId, orderId, next, employeeSession);
      setOpened(false);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: toHebrewApiErrorMessage(error instanceof Error ? error.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <UnstyledButton className={classes.strip} onClick={open} aria-label="עריכת תיאור שולחן" data-testid="table-description-strip">
        <FontAwesomeIcon icon={faComment} />
        <span className={classes.stripText}>{description ?? 'תיאור שולחן'}</span>
        <span className={classes.pencil} data-testid="table-description-edit" aria-hidden="true">
          <FontAwesomeIcon icon={faPencil as IconProp} />
        </span>
      </UnstyledButton>
      <Modal
        opened={opened}
        onClose={() => !isSaving && setOpened(false)}
        title={`תיאור לשולחן ${tableNumber ?? ''}`}
        size="md"
        yOffset={24}
        radius="md"
        dir="rtl"
        classNames={{ title: classes.title }}
        closeButtonProps={{ 'aria-label': 'סגירה', 'data-testid': 'table-description-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
      >
        <div className={classes.body}>
          <p className={classes.hint}>הערה קצרה שעוזרת לצוות לזהות את הסועדים</p>
          <TextInput
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            className={classes.input}
            aria-label="תיאור שולחן"
            maxLength={MAX_LENGTH}
            disabled={isSaving}
            data-testid="table-description-input"
            data-autofocus
            {...noSelectionTextInputProps}
          />
          <span className={classes.counter} data-testid="table-description-counter">
            {draft.length}/{MAX_LENGTH}
          </span>
          <div className={classes.chips}>
            {QUICK_WORDS.map((word, index) => (
              <UnstyledButton
                key={word}
                className={classes.chip}
                onClick={() => appendQuickWord(word)}
                disabled={isSaving}
                data-testid={`table-description-chip-${index}`}
              >
                {word}
              </UnstyledButton>
            ))}
          </div>
          <div className={classes.staffOnly}>
            <FontAwesomeIcon icon={faLock as IconProp} />
            <span>לצוות בלבד – לא מודפס בחשבון ללקוח.</span>
          </div>
          <div className={classes.actions}>
            <Button variant="cancel" fullWidth onClick={() => setDraft('')} disabled={isSaving} dataTestId="table-description-clear">
              ניקוי
            </Button>
            <Button variant="confirm" fullWidth className={classes.save} onClick={handleSave} loading={isSaving} dataTestId="table-description-save">
              שמירה
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export const TableDescription = React.memo(TableDescriptionComponent);
